import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { CalendarIcon, ArrowLeft, Send, CheckCircle2, PartyPopper, Home } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { createLead } from "@/lib/api";
import { whatsappLink, buildVehicleWaMessage } from "@/lib/config";

const WhatsAppIcon = ({ className }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
        <path d="M16.004 0h-.008C7.174 0 .002 7.174.002 16c0 3.5 1.13 6.747 3.05 9.38L1.06 31.27l6.067-1.94A15.9 15.9 0 0 0 16.004 32C24.83 32 32 24.826 32 16S24.83 0 16.004 0zm9.31 22.594c-.386 1.09-1.92 1.995-3.144 2.26-.836.178-1.928.32-5.604-1.204-4.7-1.948-7.726-6.724-7.962-7.034-.226-.31-1.9-2.53-1.9-4.826 0-2.296 1.166-3.424 1.636-3.904.386-.396.844-.576 1.126-.576.366 0 .67.004.936.018.3.014.7-.114 1.094.834.404.972 1.374 3.36 1.494 3.604.122.244.204.53.04.84-.154.31-.232.502-.46.772-.232.27-.484.602-.69.808-.232.23-.474.48-.204.94.27.46 1.2 1.98 2.578 3.208 1.77 1.578 3.262 2.066 3.722 2.296.46.232.728.194.996-.116.27-.31 1.15-1.342 1.456-1.804.302-.46.61-.384 1.026-.23.42.154 2.658 1.252 3.116 1.48.46.232.764.346.876.538.116.192.116 1.108-.27 2.176z" />
    </svg>
);

export const LeadStep = ({ vehicle, result, analysisId, onBack }) => {
    const [form, setForm] = useState({ name: "", telefon: "", email: "", plz: "", nachricht: "" });
    const [date, setDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const canSubmit = form.name.trim() && form.telefon.trim();

    const submit = async () => {
        if (!canSubmit || loading) return;
        setLoading(true);
        try {
            await createLead({
                ...form,
                wunschtermin: date ? format(date, "yyyy-MM-dd") : "",
                vehicle,
                analysis_id: analysisId,
                empfohlene_stage: result?.empfohlene_stage || "",
            });
            toast.success("Anfrage erfolgreich gesendet!");
            setDone(true);
        } catch (e) {
            toast.error("Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        const waMessage = buildVehicleWaMessage({ vehicle, stage: result?.empfohlene_stage });
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mx-auto max-w-lg text-center"
            >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                    <PartyPopper className="h-8 w-8 text-success" />
                </div>
                <h2 className="mt-6 font-heading text-2xl font-bold text-white sm:text-3xl">
                    Anfrage gesendet!
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">
                    Vielen Dank, {form.name.split(" ")[0]}! Wir haben deine Anfrage für{" "}
                    <span className="text-white">
                        {vehicle?.marke} {vehicle?.modell}
                    </span>{" "}
                    erhalten und melden uns in Kürze bei dir. Für eine sofortige Antwort kontaktiere uns
                    gerne direkt über WhatsApp.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <a
                        data-testid="success-whatsapp-btn"
                        href={whatsappLink(waMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-[#25D366] px-6 py-4 text-base font-bold text-white hover:bg-[#1eb858] transition-colors"
                    >
                        <WhatsAppIcon className="h-5 w-5" /> Jetzt per WhatsApp
                    </a>
                    <a
                        href="/"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-6 py-4 text-base font-medium text-white hover:border-primary/50 transition-colors"
                    >
                        <Home className="h-4 w-4" /> Startseite
                    </a>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-xl">
            <div className="text-center">
                <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">Angebot anfordern</h2>
                <p className="mt-2 text-sm text-[#A1A1AA]">
                    Hinterlasse deine Kontaktdaten – wir erstellen dir ein persönliches Angebot
                    {result?.empfohlene_stage ? ` für ${result.empfohlene_stage}.` : "."}
                </p>
            </div>

            <div className="mt-8 space-y-4 rounded-2xl border border-border bg-surface p-5 sm:p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <label className="mb-1.5 block text-xs text-[#A1A1AA]">Name *</label>
                        <input
                            data-testid="lead-name"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="Max Mustermann"
                            className="w-full rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs text-[#A1A1AA]">Telefon *</label>
                        <input
                            data-testid="lead-telefon"
                            value={form.telefon}
                            onChange={(e) => update("telefon", e.target.value)}
                            placeholder="+43 ..."
                            className="w-full rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs text-[#A1A1AA]">E-Mail</label>
                        <input
                            data-testid="lead-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            placeholder="max@beispiel.at"
                            className="w-full rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs text-[#A1A1AA]">PLZ</label>
                        <input
                            data-testid="lead-plz"
                            value={form.plz}
                            onChange={(e) => update("plz", e.target.value)}
                            placeholder="1010"
                            className="w-full rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                        />
                    </div>
                    <div>
                        <label className="mb-1.5 block text-xs text-[#A1A1AA]">Wunschtermin</label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    data-testid="lead-wunschtermin"
                                    className="flex w-full items-center gap-2 rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-left text-sm text-white focus:border-primary focus:outline-none transition-colors"
                                >
                                    <CalendarIcon className="h-4 w-4 text-primary" />
                                    {date ? (
                                        format(date, "dd. MMMM yyyy", { locale: de })
                                    ) : (
                                        <span className="text-[#52525B]">Datum wählen</span>
                                    )}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto border-border bg-surface p-0"
                                align="start"
                            >
                                <Calendar
                                    mode="single"
                                    selected={date}
                                    onSelect={setDate}
                                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div>
                    <label className="mb-1.5 block text-xs text-[#A1A1AA]">Nachricht</label>
                    <textarea
                        data-testid="lead-nachricht"
                        value={form.nachricht}
                        onChange={(e) => update("nachricht", e.target.value)}
                        rows={3}
                        placeholder="Weitere Wünsche oder Fragen..."
                        className="w-full resize-none rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder:text-[#52525B] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                    />
                </div>

                {vehicle?.marke && (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-[#0A0A0A] px-4 py-3 text-xs text-[#A1A1AA]">
                        <CheckCircle2 className="h-4 w-4 text-success" />
                        Fahrzeug: {vehicle.marke} {vehicle.modell}
                        {result?.empfohlene_stage ? ` · ${result.empfohlene_stage}` : ""}
                    </div>
                )}
            </div>

            <div className="mt-6 flex gap-3">
                <button
                    data-testid="lead-back-btn"
                    onClick={onBack}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface-elevated px-5 py-3.5 text-sm font-medium text-white hover:border-primary/50 transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Zurück
                </button>
                <button
                    data-testid="lead-submit-btn"
                    disabled={!canSubmit || loading}
                    onClick={submit}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 text-base font-bold text-white hover:bg-[#FF3B30] transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                >
                    <Send className="h-4 w-4" /> {loading ? "Senden..." : "Angebot anfordern"}
                </button>
            </div>
        </motion.div>
    );
};

export default LeadStep;
